import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UiState {
  /** Mobile drawer (below 900px). */
  menuOpen: boolean; setMenuOpen: (v: boolean) => void;
  /** Desktop: hide the 232px OS sidebar; the header hamburger restores it (same pattern as the sibling apps). */
  sidebarCollapsed: boolean; setSidebarCollapsed: (v: boolean) => void;
  /** Per-screen filters (date, tab, search …). Selections are transient and never persisted. */
  pages: Record<string, Record<string, unknown>>;
  setPage: (page: string, patch: Record<string, unknown>) => void;
}

const TRANSIENT = new Set(['selectedId', 'mode', 'sub', 'drill']);
const persistedPages = (pages: UiState['pages']) => Object.fromEntries(Object.entries(pages).map(([page, s]) => [page, Object.fromEntries(Object.entries(s).filter(([k]) => !TRANSIENT.has(k)))]));

export const useUi = create<UiState>()(persist((set) => ({
  menuOpen: false, setMenuOpen: (menuOpen) => set({ menuOpen }),
  sidebarCollapsed: false, setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
  pages: {},
  setPage: (page, patch) => set((s) => ({ pages: { ...s.pages, [page]: { ...(s.pages[page] ?? {}), ...patch } } })),
}), { name: 'distributor.ui', partialize: (s) => ({ sidebarCollapsed: s.sidebarCollapsed, pages: persistedPages(s.pages) }) }));

export function usePageState<T extends Record<string, unknown>>(page: string, defaults: T): [T, (patch: Partial<T>) => void] {
  const state = useUi((s) => s.pages[page]) as Partial<T> | undefined;
  const setPage = useUi((s) => s.setPage);
  return [{ ...defaults, ...(state ?? {}) }, (patch) => setPage(page, patch)];
}
